import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ConsentData, defaultConsentData } from '../../shared/models/consent-data';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    if (isPlatformBrowser(this.platformId)) {
      //send the initial consent data
      let userConsent = this.getStoredConsent();
      if (!userConsent) {
        userConsent = defaultConsentData;
      }
      this.setDefaultConsent(userConsent);
    }
  }

  private gtag(...args: any[]): void {
    if (isPlatformBrowser(this.platformId)) {
      window.dataLayer = window.dataLayer || [];
      args.forEach((arg) => {
        window.dataLayer.push(arg);
      });
    }
  }

  public sendPageViewEvent(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.gtag({
        event: 'page_view',
      });
    }
  }

  public sendAccountDeletedEvent(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.gtag({
        event: 'delete_account',
      });
    }
  }

  public sendLoginEvent(method: string): void {
    if (isPlatformBrowser(this.platformId)) {
      this.gtag({
        event: 'login',
        method: method,
      });
    }
  }

  public sendSignUpEvent(method: string): void {
    if (isPlatformBrowser(this.platformId)) {
      this.gtag({
        event: 'sign_up',
        method: method,
      });
    }
  }

  public sendBeginCheckoutEvent(currency: string, value: number, items: { item_id: string; item_name: string }[]): void {
    if (isPlatformBrowser(this.platformId)) {
      this.gtag({
        event: 'begin_checkout',
        currency: currency,
        value: value,
        items: items,
      });
    }
  }

  private setDefaultConsent(consentData: ConsentData): void {
    if (isPlatformBrowser(this.platformId)) {
      window.gtag('consent', 'default', consentData);
    }
  }

  public updateConsent(consentData: ConsentData): void {
    if (isPlatformBrowser(this.platformId)) {
      window.gtag('consent', 'update', consentData);

      // Save consent to cookie
      const expirationDate = new Date();
      expirationDate.setMonth(expirationDate.getMonth() + 6);
      document.cookie = `consentData=${JSON.stringify(consentData)};expires=${expirationDate.toUTCString()};path=/`;
    }
  }

  public getStoredConsent(): ConsentData | null {
    if (!isPlatformBrowser(this.platformId)) return null;

    const name = 'consentData=';
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) === 0) {
        const consent = JSON.parse(c.substring(name.length, c.length));
        return consent;
      }
    }
    return null;
  }
}
